import { useState } from "react";
import { endpoints, formatApiError } from "../lib/api";
import { healthMeta, healthLine, CADENCE_CHOICES } from "../lib/crmHealth";
import { PhoneCall, Envelope, CalendarCheck, CircleNotch, X, Clock, Bell } from "@phosphor-icons/react";

const TYPES = [
    { v: "call", label: "Call", icon: PhoneCall },
    { v: "email", label: "Email", icon: Envelope },
    { v: "meeting", label: "Visit / meeting", icon: CalendarCheck },
];

export function HealthChip({ health, compact = false }) {
    if (!health) return null;
    const m = healthMeta(health.status);
    const text = compact
        ? health.status === "overdue" ? `No contact ${health.days_since_contact}d`
            : health.status === "never" ? "Never contacted"
            : health.status === "due_soon" ? "Contact due soon" : m.label
        : m.label;
    return (
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap" style={{ color: m.color, background: m.bg }} data-testid="health-chip">
            {text}
        </span>
    );
}

// Small inline form: log a call / email / visit against an account.
export function QuickLogContact({ clientId, onLogged, onCancel, autoFocus = true }) {
    const [type, setType] = useState("call");
    const [subject, setSubject] = useState("");
    const [busy, setBusy] = useState(false);
    async function save() {
        setBusy(true);
        try {
            const label = TYPES.find((t) => t.v === type)?.label || "Contact";
            await endpoints.addClientActivity(clientId, { type, subject: subject.trim() || `${label} logged` });
            onLogged?.();
        } catch (e) { alert(formatApiError(e)); }
        finally { setBusy(false); }
    }
    return (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="quick-log-contact">
            <div className="inline-flex rounded-[4px] border border-[var(--rom-border-strong)] overflow-hidden">
                {TYPES.map((t) => {
                    const I = t.icon;
                    return (
                        <button key={t.v} type="button" onClick={() => setType(t.v)} title={t.label} data-testid={`quick-log-type-${t.v}`}
                            className={`px-2 h-8 flex items-center ${type === t.v ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)]"}`}>
                            <I size={13} weight="bold" />
                        </button>
                    );
                })}
            </div>
            <input className="input h-8 flex-1 min-w-[160px]" autoFocus={autoFocus} placeholder="What was it about? (optional)" value={subject}
                onChange={(e) => setSubject(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} data-testid="quick-log-subject" />
            <button className="btn btn-primary btn-sm" onClick={save} disabled={busy} data-testid="quick-log-save">
                {busy ? <CircleNotch size={13} className="animate-spin" /> : "Log"}
            </button>
            {onCancel && <button className="text-[var(--rom-text-3)] hover:text-[var(--rom-text)]" onClick={onCancel}><X size={14} /></button>}
        </div>
    );
}

// Account page banner: contact status, cadence override, quick log.
export function ContactHealthBar({ clientId, health, onChanged }) {
    const [logging, setLogging] = useState(false);
    if (!health) return null;
    const m = healthMeta(health.status);
    const value = health.cadence_custom ? String(health.cadence_days) : "";

    async function setCadence(v) {
        try { await endpoints.patchClient(clientId, { contact_every_days: v === "" ? null : Number(v) }); onChanged(); }
        catch (e) { alert(formatApiError(e)); }
    }

    return (
        <div className="rounded-[6px] border px-4 py-2.5 flex items-center gap-3 flex-wrap" style={{ borderColor: m.color, background: m.bg }} data-testid="contact-health-bar">
            <Bell size={15} weight="bold" style={{ color: m.color }} />
            <div className="text-sm flex-1 min-w-[220px]">
                <span className="font-bold" style={{ color: m.color }}>{m.label}</span>
                <span className="text-[var(--rom-text-2)]"> · {healthLine(health)}</span>
                {health.last_contact_by && health.last_contact_at && <span className="text-[var(--rom-text-3)] text-xs"> · {health.last_contact_type} by {health.last_contact_by}</span>}
            </div>
            <label className="flex items-center gap-1.5 text-xs text-[var(--rom-text-2)]">
                <Clock size={12} weight="bold" /> Contact every
                <select value={value} onChange={(e) => setCadence(e.target.value)} data-testid="cadence-select"
                    className="h-7 px-1.5 rounded-[4px] border border-[var(--rom-border-strong)] text-xs bg-white font-semibold">
                    <option value="">Stage default{!health.cadence_custom ? ` (${health.cadence_days || "off"}${health.cadence_days ? "d" : ""})` : ""}</option>
                    {CADENCE_CHOICES.map((d) => <option key={d} value={String(d)}>{d} days</option>)}
                    <option value="0">Don't flag</option>
                </select>
            </label>
            {logging
                ? <div className="w-full"><QuickLogContact clientId={clientId} onLogged={() => { setLogging(false); onChanged(); }} onCancel={() => setLogging(false)} /></div>
                : <button className="btn btn-sm bg-white" onClick={() => setLogging(true)} data-testid="health-log-contact-btn"><PhoneCall size={12} weight="bold" /> Log contact</button>}
        </div>
    );
}
